import { useCallback, useEffect, useState } from "react";

import {
  fetchWhoopBody,
  fetchWhoopCycles,
  fetchWhoopProfile,
  fetchWhoopRecovery,
  fetchWhoopSleep,
  fetchWhoopWorkouts,
} from "./devices-api";
import type { WhoopAllData } from "./devices-types";

export interface UseDeviceDataReturn {
  data: WhoopAllData;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const EMPTY_DATA: WhoopAllData = {
  profile: null,
  body: null,
  recovery: [],
  sleep: [],
  workouts: [],
  cycles: [],
};

export function useDeviceData(connected: boolean): UseDeviceDataReturn {
  const [data, setData] = useState<WhoopAllData>(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!connected) return;
    setIsLoading(true);
    setError(null);

    try {
      const [profile, body, recovery, sleep, workouts, cycles] = await Promise.all([
        fetchWhoopProfile(),
        fetchWhoopBody(),
        fetchWhoopRecovery(),
        fetchWhoopSleep(),
        fetchWhoopWorkouts(),
        fetchWhoopCycles(),
      ]);
      setData({ profile, body, recovery, sleep, workouts, cycles });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load device data");
    } finally {
      setIsLoading(false);
    }
  }, [connected]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const refresh = useCallback(() => {
    void loadData();
  }, [loadData]);

  return { data, isLoading, error, refresh };
}
